import {ChangeDetectorRef, Component, NgZone, OnInit, Pipe, PipeTransform} from '@angular/core';
import { TariffService } from '@accounts/user/services/tariff.service';
import { GasStationModel, TrailerModel } from '@core/api';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { Router } from '@angular/router';
import { UserService as AuthUserService } from '@core/auth';
import { StationService } from '@accounts/user/services/station.service';
import { BottomPopupService } from '@ui/bottom-popup/bottom-popup.service';
import { MatBottomSheetRef } from '@angular/material';
import { MapService } from '@accounts/user/services/map.service';

interface LocationGroup {
  name: string;
  stations: GasStationModel[];
}

@Component({
  selector: 'popup-filters-list',
  templateUrl: './filters-list.component.html',
  styleUrls: ['./filters-list.component.scss']
})
export class FiltersListComponent implements OnInit {
  constructor(
    private router: Router,
    public stationService: StationService,
    private bottomSheetRef: MatBottomSheetRef<FiltersListComponent>,
    private cdr: ChangeDetectorRef,
    private mapService: MapService,
  ) {}

  filterList: TrailerModel.TypeEnum[] = [];
  selectedCity: string | null = null;
  returnLocationId: number | null = null;
  stations: GasStationModel[] = [];
  cities: string[] = [];
  cityStations: GasStationModel[] = [];
  returnLocations: LocationGroup[] = [];
  loading: boolean = true;

  trailersTypes = [
    { name: 'Тентовый', type: TrailerModel.TypeEnum.WithTent },
    { name: 'Без тента', type: TrailerModel.TypeEnum.WithoutTent }
  ];

  changeFilter(filter: TrailerModel.TypeEnum) {
    if (this.filterList.includes(filter)) {
      this.filterList = this.filterList.filter(x => x != filter);
    } else {
      this.filterList.push(filter);
    }
  }

  selectCity(city: string) {
    this.selectedCity = city;
    this.returnLocationId = null; // Reset return location when city changes
    
    // Get all stations in the selected city
    this.cityStations = this.stations.filter(s => s.location_group === city);
    
    // Load return locations based on stations in this city
    this.loadReturnLocations();
    
    // Apply filter to show all stations in the selected city
    this.applyLocationFilter();
  }

  loadReturnLocations() {
    if (!this.cityStations.length) return;
    
    this.loading = true;
    this.returnLocations = [];
    
    // Get the network_id from the first station in the city
    const networkId = this.cityStations[0].network_id;
    
    // Load stations with available transit spaces for the network
    // This returns stations that can accept trailers from other locations
    this.stationService.getGasStationsWithTransitSpaces(networkId).subscribe(
      (data) => {
        if (data.success) {
          // Start with stations that have transit spaces (can accept from other locations)
          let returnStations = data.data;
          
          // IMPORTANT: Always add the rental city stations themselves
          // Users can always return to the same location where they rented
          // But we need to filter out other stations from the same location_group
          // So first filter, then add back the rental stations
          
          // Filter out stations with the same location_group as the selected city
          if (this.selectedCity) {
            returnStations = returnStations.filter(
              station => station.location_group !== this.selectedCity
            );
          }

          // Add rental stations back after filtering
          this.cityStations.forEach(cityStation => {
            if (!returnStations.find(s => s.id === cityStation.id)) {
              returnStations.push(cityStation);
            }
          });

          // Group return stations by location_group (city)
          returnStations.forEach(station => {
            if (station.location_group === null) {
              station.location_group = 'Другие';
            }

            if (!this.returnLocations.find(location => location.name === station.location_group)) {
              this.returnLocations.push({
                name: station.location_group,
                stations: [],
              });
            }

            this.returnLocations.find(location => location.name === station.location_group).stations.push(station);
          });

          // Sort cities: selected city first, then Минск, then alphabetically, Другие last
          this.returnLocations.sort((a, b) => {
            if (a.name === this.selectedCity) return -1;
            if (b.name === this.selectedCity) return 1;
            if (a.name === "Минск") return -1;
            if (b.name === "Минск") return 1;
            if (a.name === "Другие") return 1;
            if (b.name === "Другие") return -1;

            return a.name.localeCompare(b.name, "ru", { sensitivity: "base" });
          });

          this.loading = false;
          this.cdr.detectChanges();
        }
      },
      (error) => {
        console.error('Ошибка загрузки станций возврата:', error);
        this.loading = false;
      }
    );
  }

  selectReturnLocation(station: GasStationModel) {
    this.returnLocationId = station.id;
    this.applyLocationFilter();
  }

  clearCity() {
    this.selectedCity = null;
    this.cityStations = [];
    this.returnLocations = [];
    this.applyLocationFilter();
  }

  clearReturnLocation() {
    this.returnLocationId = null;
    this.applyLocationFilter();
  }

  closePopup() {
    this.bottomSheetRef.dismiss();
  }

  applyLocationFilter() {
    // Pass city stations and return location to the service
    const cityStationIds = this.cityStations.map(s => s.id);
    this.stationService.setLocationFilter(cityStationIds, this.returnLocationId);
  }

  getStationById(id: number): GasStationModel | undefined {
    // Search in all stations (both rental and return)
    let station = this.stations.find(s => s.id === id);
    if (!station) {
      // Search in return locations
      for (const group of this.returnLocations) {
        station = group.stations.find(s => s.id === id);
        if (station) break;
      }
    }
    return station;
  }

  ngOnInit() {
    // Load available stations and extract unique cities
    this.stationService.getNStations(50).subscribe(data => {
      this.stations = data;

      // Extract unique cities (location_groups)
      const citySet = new Set<string>();
      this.stations.forEach(station => {
        const city = station.location_group || 'Другие';
        citySet.add(city);
      });

      this.cities = Array.from(citySet).sort((a, b) => {
        if (a === "Минск") return -1;
        if (b === "Минск") return 1;
        if (a === "Другие") return 1;
        if (b === "Другие") return -1;
        return a.localeCompare(b, "ru", { sensitivity: "base" });
      });

      this.loading = false;
      this.cdr.detectChanges();

      // Get current filter values and restore if city was selected
      const currentFilter = this.stationService.locationFilter;
      if (currentFilter.cityStationIds && currentFilter.cityStationIds.length > 0) {
        // Find the city from the station IDs
        const firstStation = this.stations.find(s => s.id === currentFilter.cityStationIds[0]);
        if (firstStation) {
          this.selectedCity = firstStation.location_group || 'Другие';
          this.cityStations = this.stations.filter(s => 
            currentFilter.cityStationIds.includes(s.id)
          );
          this.returnLocationId = currentFilter.returnLocationId;
          this.loadReturnLocations();
        }
      }
    });
  }

}